import type { AbsencePercentage } from './absence';

export interface LabelPasswordAuthResponse {
	token: string;
	label_slug: string;
	message: string;
}

export interface Quote {
	quote_id: string;
	insurer: string;
	insurer_name: string;
	image_key?: string;
	premium_rate_percentage: number | null | undefined;
	amount: number | null;
	total: number | null;
	saving_percentage: number | null;
	saving_amount: number | null;
	originalIndex?: number;
	isBestOffer?: boolean;
	isSelected?: boolean;
	[key: string]: unknown;
}

export interface OccupationalHealthServiceQuote {
	quote_id: string;
	provider_name: string;
	provider_slug: string;
	package_name: string;
	payoff: string;
	url: string;
	rate: string;
}

/**
 * Categorizes why a policy document could not be processed. Mirrors the
 * backend `UnreadableReasonLiteral`. Used by the `/polis-niet-leesbaar`
 * error page to render a targeted message and recovery hint.
 */
export type UnreadableReason =
	| 'NO_POLICY_DETECTED'
	| 'WRONG_PRODUCT_TYPE'
	| 'INCOMPLETE_DOCUMENT'
	| 'PROTECTED_PDF'
	| 'TECHNICAL_FAILURE'
	| 'KVK_UNAVAILABLE'
	| 'SME_ABSENCE_RELIEF';

/**
 * Non-blocking warnings about the recognized current policy. Mirrors the
 * backend `PolicyWarningLiteral`. Surfaced alongside the normal quote flow
 * so the frontend can render banners (e.g. when the current policy is an
 * MKB Verzuim-Ontzorgverzekering bundle and the proposed quotes are for
 * plain Ziekteverzuim Conventioneel).
 */
export type PolicyWarning = 'SME_ABSENCE_RELIEF_BUNDLE';

/**
 * Company details for the quote request. Mirrors the backend `CompanyDetails`
 * model and is carried (nested) by the stream snapshot / full GET payload.
 * Distinct from the flat `company_name`/`coc`/… fields on `AppData`.
 */
export interface CompanyInfo {
	company_name?: string | null;
	phone?: string | null;
	address?: string | null;
	zip_code?: string | null;
	city?: string | null;
	sector_code?: string | null;
	sector_name?: string | null;
	coc?: string | null;
	sbi1?: string | null;
	sbi2?: string | null;
	sbi3?: string | null;
	coc_registration_date?: string | null;
}

export interface AppData {
	id?: string;
	status?: string;
	unreadable_reason?: UnreadableReason | null;
	policy_warnings?: PolicyWarning[];
	expected_num_quotes?: number;
	// True once a current-policy PDF has been uploaded (at start or later). False
	// for fileless no-policy / initial-payload forms — the only ones that show the
	// "add your policy later" CTA. Distinct from `current_policy`, which is null
	// while a just-uploaded document is still extracting (issue #961).
	has_current_policy_document?: boolean;
	policy_upload_form_id?: string;
	access_token?: string;
	temp_redirect_url?: string;
	company_name?: string;
	coc?: string;
	sector_name?: string;
	address?: string;
	zip_code?: string;
	city?: string;
	company_info?: CompanyInfo;
	current_policy?: {
		verzekeraar?: string | null;
		pakket?: string | null;
		loonsom?: number | null;
		werknemer_aantal?: number | null;
		sick_leave_short_term?: {
			premium_rate_percentage?: number | null;
			insurer?: string | null;
			insurer_name?: string | null;
			coverage?: {
				waiting_period?: string | number;
				employer_costs?: number;
				coverage_percentage_first_year_first_half?: number;
				coverage_percentage_first_year_second_half?: number;
				coverage_percentage_second_year_first_half?: number;
				coverage_percentage_second_year_second_half?: number;
				[key: string]: unknown;
			} | null;
			[key: string]: unknown;
		};
		[key: string]: unknown;
	};
	quotes?: {
		sick_leave_short_term?: Quote[];
		occupational_health_service?: OccupationalHealthServiceQuote[];
	};
	premium_variables?: {
		employees?: {
			employee_list?: Array<{
				name?: string;
				gender?: string;
				birth_date?: string;
				wage?: number;
				contract_type?: string;
				[key: string]: unknown;
			}>;
			source?: string | null;
		};
		absence_info?: {
			source?: string | null;
			rates_by_year?: Record<string, unknown>;
			absence_percentage_list?: AbsencePercentage[];
			[key: string]: unknown;
		};
		coverage?: {
			sick_leave_short_term?: {
				coverage_percentage_first_year_first_half?: number;
				coverage_percentage_first_year_second_half?: number;
				coverage_percentage_second_year_first_half?: number;
				coverage_percentage_second_year_second_half?: number;
				employer_costs?: number;
				waiting_period?: number;
				source?: string | null;
				[key: string]: unknown;
			};
		};
	};
	activation_redirect_url?: string;
	relevant_product_types?: string[];
	[key: string]: unknown;
}

export interface ApplicationData {
	quote_id: string | null;
	initials: string | null;
	prefix: string | null;
	lastName: string | null;
	email: string | null;
	phone: string | null;
	arboService: boolean;
	arbodienstverlener: string | null;
	agreeToTerms: boolean | null;
	agreeToCancel: boolean | null;
	hasNoLegalIssues: boolean | null;
	hasNoInsuranceIssues: boolean | null;
	hasNoBankruptcyIssues: boolean | null;
	selected_sick_leave_short_term_quote_id: string | null;
	selected_occupational_health_service_quote_id: string | null;
	selected_wga_gap_extended_quote_id: string | null;
	selected_wga_gap_quote_id: string | null;
	selected_wia_excess_quote_id: string | null;
	selected_wia_excess_dekkingspercentage?: string | null;
	[key: string]: unknown;
}

/**
 * Configuration data for the application, loaded from the backend `/config` endpoint.
 *
 * @remarks
 * The `products` map was removed from `/config` in backend PR #984.
 * Product visibility is now driven by `relevant_product_types` on each
 * `QuoteListResponse` / `AppData`. Do not use `config.products` for
 * runtime product visibility decisions.
 */
/**
 * Feature flags exposed by the backend `/config` endpoint.
 *
 * @remarks
 * The endpoint returns these as `active_feature_flags`, a list containing only
 * the enabled flag names (see {@link ConfigData}). A flag is enabled iff it is
 * present in that list; an absent flag is disabled. Use {@link isFeatureActive}
 * to read a flag rather than touching the list directly.
 */
export enum FeatureFlag {
	ALLOW_USER_COVERAGE_OVERRIDE = 'allow_user_coverage_override',
	ALLOW_EMPLOYEE_EXTRACTION = 'allow_employee_extraction',
	ENABLE_LANDING_PAGE_EXPLAINER = 'enable_landing_page_explainer',
	ENABLE_MULTI_LANGUAGE = 'enable_multi_language',
	ENABLE_PROVIDER_RATINGS = 'enable_provider_ratings',
	ENABLE_PROACTIVE_CONTACT_DIALOG = 'enable_proactive_contact_dialog',
	ALLOW_NO_POLICY_FLOW = 'allow_no_policy_flow',
	ENABLE_SHARE_WITH_ADVISOR = 'enable_share_with_advisor',
	ALLOW_DIRECT_APPLICATION = 'allow_direct_application'
}

export interface ConfigData {
	id?: string;
	handle?: string;
	nicename?: string;
	/**
	 * When true, the session is inspection-only (admin/team dossier links).
	 * Advisor/client links are writable (`false`).
	 */
	read_only_mode?: boolean;
	/** Names of the feature flags that are enabled for this origin. */
	active_feature_flags?: FeatureFlag[];
	website?: {
		homepage?: string;
		[key: string]: unknown;
	};
	theme?: {
		primary?: string;
		'bg-body-alt'?: string;
		'headings-font-weight'?: string;
		[key: string]: string | undefined;
	};
	[key: string]: unknown;
}

export type {
	AbsenceInfo,
	AbsencePercentage,
	AbsenceValues,
	PendingPremiumVars
} from './absence';
export type {
	AbsencePayload,
	ActivationResponse,
	ApplicationResponse,
	ApplicationUpdatePayload,
	CocLookupPayload,
	CocPayload,
	CocSearchResponse,
	CocSearchResultItem,
	ContactPayload,
	CoveragePayload,
	EmployeeImportResponse,
	EmployeePayload,
	FormDataFields,
	HeaderOptions,
	PolicyUploadResponse,
	PremiumPercentagePayload,
	PremiumVarsPayload,
	ProductTypeCoveragePayload,
	QuoteItem,
	QuoteListResponse,
	SectorCodePayload,
	TokenRefreshResponse,
	WageInfoPayload
} from './api';
export { SupportedProductType } from './api';
export type { CoverageInfo, CoverageValues } from './coverage';
// Re-export types from other modules for convenience
export type { Employee, EmployeeSummary, ExtractionResponse } from './employee';
export { DEFAULT_EMPLOYEE } from './employee';
export type { AlertVariant, Color, SettingType, Size } from './ui';
export type { AgeRange, EmployeeCounts, SalaryRange, SavingPercentages } from './utils';
